import { useEffect, useState } from "react";
import { Badge, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import FiscalDocumentDownload from "./FiscalDocumentDownload.jsx";
import api from "@/api/api.js";
import { FISCAL_DOCUMENTS, complianceTone } from "@/utils/fiscalDocuments.js";
import { formatShortDate } from "@/utils/formatDate.js";

/**
 * La tarjeta del Inicio para un documento fiscal.
 *
 * Sólo se pinta si el paquete del usuario lo incluye —eso lo decide quien la
 * usa—, y mientras el contador no lo haya subido el botón se ve pero no se
 * puede oprimir, con el motivo a la vista en vez de una tarjeta vacía.
 */
const FiscalDocumentCard = ({ companyId, type }) => {
  const { t } = useTranslation();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const { route, titleKey, icon } = FISCAL_DOCUMENTS[type];
  const showStatus = type === "complianceOpinion";

  useEffect(() => {
    if (!companyId) return undefined;

    let active = true;
    setLoading(true);
    api
      .get(`/companies/${companyId}/${route}/latest`)
      .then(({ data }) => active && setDocument(data))
      // 404 mientras el contador no lo suba; 403 si el paquete dejó de incluirlo.
      .catch(() => active && setDocument(null))
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [companyId, route]);

  const status = document?.complianceStatus;

  return (
    <div className="col-xxl-4 col-md-6">
      <div className="card info-card">
        <div className="card-body">
          <h5 className="card-title">{t(titleKey)}</h5>
          <div className="d-flex align-items-center">
            <div className="card-icon rounded-circle d-flex align-items-center justify-content-center">
              <i className={`bi ${icon}`} />
            </div>
            <div className="ps-3 w-100">
              {loading ? (
                <span className="text-muted">
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t("ui.loading")}
                </span>
              ) : (
                <>
                  {document ? (
                    <>
                      <p className="mb-2 text-muted small">
                        {t("ui.updatedOn", {
                          date: formatShortDate(document.createdAt),
                        })}
                      </p>
                      {showStatus && (
                        <p className="mb-2">
                          <Badge bg={complianceTone(status).variant}>
                            <i
                              className={`bi ${complianceTone(status).icon} me-1`}
                            />
                            {t(`ui.compliance.${status}`)}
                          </Badge>
                          {status === "sin_determinar" && (
                            <span className="ms-2 text-muted small">
                              {t("ui.askYourAccountant")}
                            </span>
                          )}
                        </p>
                      )}
                      <FiscalDocumentDownload
                        companyId={companyId}
                        documentId={document.id}
                        type={type}
                        fileName={document.fileIdentifier}
                        label={t("ui.download")}
                        className="btn btn-sm btn-primary"
                      />
                    </>
                  ) : (
                    <>
                      <button
                        className="btn btn-sm btn-secondary"
                        type="button"
                        disabled
                      >
                        <i className="bi bi-download me-1" />
                        {t("ui.download")}
                      </button>
                      <p className="mb-0 mt-2 text-muted small">
                        {t("ui.pendingFromAccountant")}
                      </p>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FiscalDocumentCard;
